import { Color4, Vector3 } from '@babylonjs/core/Maths/math';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import {
    DimensionNode,
    Kb3dObject,
    SpaceNode,
    eAnnotationLineStyle,
    eDimensionAxis,
    eDimensionLineCapStyle,
} from '@kb3d/models/classes';
import { AnnotationNode } from '@kb3d/models/classes/annotations/annotation-node';
import { Connector } from '@kb3d/models/classes/features/connector';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { getCameraPositionWithOffset, rotationToVector, transformVectorWithQuat } from '@kb3d/view/helpers/math-helper';
import { Subscription, merge } from 'rxjs';
import { AnnotationInfo } from '.';
import { KbViewer } from '../kb-viewer';
import {
    AnnotationParts,
    ILineMeshOptions,
    calculateTransform,
    createAnnotationTextModels,
    drawLine,
    getPositionFromConnnector,
    updateAnnotationText,
} from './annotation';
import { Renderer } from './renderer';
import { RendererManager } from './renderer-manager';
import { KbArcRotateCamera } from '@kb3d/view/modules/cameras/kb-arc-rotate-camera';
import { Camera } from '@babylonjs/core/Cameras/camera';
//import { Mesh } from "@babylonjs/core/Meshes/mesh"; // Used for changing the text into a billboard using the billboard property built into babylon

export interface ICapMeshOptions {
    lines: Vector3[][];
    updatable?: boolean;
    colors?: Color4[][];
    instance?: LinesMesh;
}

interface DimensionParts extends AnnotationParts {
    line: ILineMeshOptions;
    lineCap: ICapMeshOptions;
    distance: number;
}

export interface DimensionInfo extends AnnotationInfo {
    meshInfo?: DimensionParts;
    canCreate: boolean;
    boundingInfo?: {
        targetNode: Kb3dObject;
        targetNode2: Kb3dObject;
        subscription: Subscription;
    };
}

export interface scalingDashesOptions {
    distance: number;
}

/**
 * Move line position calculation to private function so it can be called from bbSubscription
 * Move text font family creation stuff to common function and call it from here with midpoint of line
 * Store the line mesh in annotationStore, just recreate it from the private function when something changes
 */

export class DimensionRenderer extends Renderer<DimensionNode, TransformNode> {
    utilityLayer: UtilityLayerRenderer;
    modelToken = Token.DimensionNode;

    watchPosition = new Map<
        string,
        {
            node: ITrackedClass<AnnotationNode>;
            containerMesh: TransformNode;
            targetNode: Kb3dObject;
        }
    >();
    dimensionStore = new Map<string, DimensionInfo>();
    billboards = new Map<string, DimensionParts>();
    lastSnappedDistances = new Map<string, number>();

    //Text in "Billboard" mode (calculated manually)
    constructor(protected viewer: KbViewer, protected scene: Scene, protected rendererManager: RendererManager) {
        super(viewer, scene);
        this.utilityLayer = viewer.utilityLayer;
        scene.registerBeforeRender(() => {
            if (scene.activeCamera && this.dimensionStore.size > 0) {
                this.billboards.forEach(meshInfo => {
                    const camera = scene.activeCamera!;
                    meshInfo.rootMesh.lookAt(getCameraPositionWithOffset(camera), 0, Math.PI / 2);
                });
            }
        });
    }

    update3dInternal(node: ITrackedClass<DimensionNode>) {

        let dimensionInfo = this.dimensionStore.get(node._dynamicId);

        if (!dimensionInfo) {

            dimensionInfo = {
                positionNeedsUpdate: false,
                textNeedsUpdate: false,
                lineNeedsUpdate: false,
                canCreate: false,
            };

            this.dimensionStore.set(node._dynamicId, dimensionInfo);
        }

        const rootMesh = dimensionInfo.meshInfo?.rootMesh;

        if (rootMesh) {

            const { scaleLinesWithCamera, scaleTextWithCamera, lineStyle } = node;

            if (!scaleLinesWithCamera && !scaleTextWithCamera) {

                this.viewer.camera.clearMaintainedNodeScreenSize(rootMesh);
                dimensionInfo.lineNeedsUpdate = true;
                dimensionInfo.textNeedsUpdate = true;
                this.lastSnappedDistances?.delete(node._dynamicId);
            } else {
                this.viewer.camera.maintainNodeScreenSize(rootMesh, (dist: number, mode: number) => {

                    if (!dimensionInfo || !dimensionInfo.meshInfo) return;

                    const cam = this.viewer.scene.activeCamera!;
                    const center = dimensionInfo.meshInfo.rootMesh.getAbsolutePosition();

                    let effectiveDist: number;

                    if (cam instanceof KbArcRotateCamera && cam.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                        effectiveDist = cam.orthoWidth;
                    } else {
                        const camPos = getCameraPositionWithOffset(cam);
                        effectiveDist = Vector3.Distance(camPos, center);
                    }

                    // Normalization by model size (distance between connectors):
                    const refSize = Math.max(dimensionInfo.meshInfo.distance, 1e-6);
                    let normalizedDist = effectiveDist / refSize;

                    // Logarithmic smoothing (help with very large models):
                    normalizedDist = Math.log2(normalizedDist + 1);

                    // Increases sensitivity:
                    const gamma = 2.5;
                    normalizedDist = Math.pow(normalizedDist, gamma);

                    const snappedDist = normalizedDist;

                    // Only if you want steps --->
                    // const step = 5;
                    // snappedDist = Math.round(normalizedDist / step) * step;
                    // <---

                    this.lastSnappedDistances = this.lastSnappedDistances || new Map<string, number>();
                    const lastDist = this.lastSnappedDistances.get(node._dynamicId);

                    if (lastDist === snappedDist) {
                        return; // Skip redundant update (both text & lines)
                    }

                    this.lastSnappedDistances.set(node._dynamicId, snappedDist);

                    const { meshInfo } = dimensionInfo;

                    // Text scaling
                    if (scaleTextWithCamera) {
                        this.updateDimensionText(node, meshInfo);
                        this.updateDimensionControlPoints(node, dimensionInfo);
                        this.updateLineCaps(node, meshInfo, this.scene);
                    }
                    else {
                        dimensionInfo.textNeedsUpdate = true;
                    }

                    // Line scaling
                    if (scaleLinesWithCamera) {
                        if (lineStyle === eAnnotationLineStyle.dashed) {
                            const options: scalingDashesOptions = {
                                distance: snappedDist,
                            };

                            this.updateLineFromCamera(node, meshInfo, scene, options);
                        }
                        else {
                            dimensionInfo.lineNeedsUpdate = true;
                        }
                    }
                    else {
                        dimensionInfo.lineNeedsUpdate = true;
                    }

                });

                // --- PRIME ONCE so dashed lines are visible on first load ---
                if (scaleLinesWithCamera && lineStyle === eAnnotationLineStyle.dashed && dimensionInfo?.meshInfo) {
                    let initialDist = 1;

                    try {
                        const cam = this.viewer.scene.activeCamera as any;
                        const camPos = cam?.globalPosition ?? cam?.position;
                        const center = rootMesh.getAbsolutePosition();

                        if (camPos && center) {
                            if (cam instanceof KbArcRotateCamera && cam.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                                initialDist = cam.orthoWidth; // Use orthoWidth for ortho cameras
                            }
                            else {
                                initialDist = Vector3.Distance(camPos, center);
                            }
                        }
                    }
                    catch {
                        /* ignore */
                    }

                    // Only if you want steps --->
                    //const step = 5;
                    //const snappedInitial = Math.round(initialDist / step) * step;
                    // <---

                    const refSizePrime = Math.max(dimensionInfo.meshInfo.distance, 1e-6);
                    let normalizedInitial = initialDist / refSizePrime;
                    normalizedInitial = Math.log2(normalizedInitial + 1);

                    const gamma = 2.5;
                    normalizedInitial = Math.pow(normalizedInitial, gamma);

                    const snappedInitial = normalizedInitial;

                    const primeOptions: scalingDashesOptions = {
                        distance: snappedInitial
                    };
                    // Important: do NOT write to lastSnappedDistances here.
                    // We want the first callback to still run and set the cache properly.
                    this.updateLineFromCamera(node, dimensionInfo.meshInfo, this.viewer.scene, primeOptions);
                }
            }
        }

        if (dimensionInfo.meshInfo && node.changes.has('keepOnTop')) {
            this.delete3d(node, true);
            dimensionInfo.meshInfo = undefined;
            dimensionInfo.positionNeedsUpdate = true;
            dimensionInfo.textNeedsUpdate = true;
            dimensionInfo.lineNeedsUpdate = true;
        }
        const scene = node.keepOnTop ? this.utilityLayer.utilityLayerScene : this.scene;

        if (!dimensionInfo.meshInfo && node.enabled && node.targetId) {
            dimensionInfo.meshInfo = {
                ...createAnnotationTextModels(node, scene, this.viewer.options.editorMode),
                line: {
                    points: [],
                    colors: [],
                    updatable: true,
                },
                lineCap: {
                    lines: [],
                    colors: [],
                    updatable: true,
                },
                distance: 0,
            };

            // Text in "Billboard" mode (using the billboard property built into babylon) --->
            //dimensionInfo.meshInfo.textMesh.rotationQuaternion = null;
            //dimensionInfo.meshInfo.textMesh.rotation = new Vector3(0, 0, 0);
            //dimensionInfo.meshInfo.textMesh.billboardMode = Mesh.BILLBOARDMODE_ALL;

            // Put the dimensions on the parent scene so the scene visibility affects the node
            if (node._parentScene) {
                const parentSceneMesh = this.viewer.getRendererNode(node._parentScene);
                dimensionInfo.meshInfo.rootMesh.setParent(parentSceneMesh);
            }

            this.set3d(node, dimensionInfo.meshInfo.rootMesh);
        }
        const meshInfo = dimensionInfo.meshInfo;
        // Set position of annotation
        if (
            node.changes.hasAny(
                'enabled',
                'targetId',
                'target2Id',
                'offset',
                'billboard',
                'labelOffset',
                'axis',
                'dimensionGap'
            )
        ) {
            dimensionInfo.positionNeedsUpdate = true;
        }

        if (
            node.changes.hasAny(
                'scaleTextWithCamera'
            )
        ) {
            const meshInfo = dimensionInfo.meshInfo;
            if (meshInfo) {
                if (node.scaleTextWithCamera) {
                    const snappedDist = this.lastSnappedDistances.get(node._dynamicId);
                    const options = {
                        distance: snappedDist
                    } as scalingDashesOptions;
                    this.updateLineFromCamera(node, meshInfo, scene, options);
                }
            }
        }

        if (node.changes.hasAny('color', 'lineStyle', 'lineCapStyle', 'scaleLinesWithCamera')) {
            dimensionInfo.lineNeedsUpdate = true;
        }

        if (
            node.changes.hasAny(
                'fontFamily',
                'fontSize',
                'label',
                'color',
                'targetId',
                'target2Id',
                'dimensionUnitPrecision'
            )
        ) {
            dimensionInfo.textNeedsUpdate = true;
        }

        if (meshInfo) {
            if (node.changes.hasAny('enabled', 'billboard', 'keepOnTop')) {
                if (node.enabled && node.billboard) {
                    this.billboards.set(node._dynamicId, meshInfo);
                } else {
                    this.billboards.delete(node._dynamicId);
                }
            }

            if (node.enabled) {
                if (dimensionInfo.positionNeedsUpdate || dimensionInfo.lineNeedsUpdate) {
                    dimensionInfo.canCreate = this.updateDimensionControlPoints(node, dimensionInfo);

                    // If the line is not getting rebuilt, then just update the line position
                    if (dimensionInfo.canCreate && !dimensionInfo.lineNeedsUpdate) {
                        meshInfo.line.instance = drawLine(node, meshInfo.line, 1, scene);
                        meshInfo.lineCap.instance = MeshBuilder.CreateLineSystem(
                            `${node._dynamicId}_lineCap`,
                            meshInfo.lineCap,
                            scene
                        );
                        meshInfo.lineCap.instance.isPickable = node.isPickable;
                    }

                    dimensionInfo.positionNeedsUpdate = false;
                }

                if (dimensionInfo.canCreate) {
                    if (dimensionInfo.lineNeedsUpdate) {
                        if (meshInfo.line.instance) {
                            meshInfo.line.instance.dispose();
                            delete meshInfo.line.instance;
                        }
                        if (meshInfo.lineCap.instance) {
                            meshInfo.lineCap.instance.dispose();
                            delete meshInfo.lineCap.instance;
                        }

                        this.updateDimensionLineColor(node, meshInfo.line, meshInfo.lineCap);

                        meshInfo.line.instance = drawLine(node, meshInfo.line, meshInfo.distance, scene);

                        if (node.lineCapStyle !== eDimensionLineCapStyle.none) {
                            const lineCapsMesh = MeshBuilder.CreateLineSystem(
                                `${node._dynamicId}_lineCap`,
                                meshInfo.lineCap,
                                scene
                            );
                            lineCapsMesh.isPickable = node.isPickable;
                            if (!meshInfo.lineCap.instance) {
                                meshInfo.lineCap.instance = lineCapsMesh;
                            }
                        }

                        // Put the lines on the parent scene so they get its visibility properties
                        if (node._parentScene) {
                            const parentSceneMesh = this.viewer.getRendererNode(node._parentScene);
                            if (meshInfo.line.instance) {
                                meshInfo.line.instance.parent = parentSceneMesh;
                            }
                            if (meshInfo.lineCap.instance) {
                                meshInfo.lineCap.instance.parent = parentSceneMesh;
                            }
                        }

                        dimensionInfo.lineNeedsUpdate = false;
                    }

                    if (dimensionInfo.textNeedsUpdate) {
                        this.updateDimensionText(node, meshInfo);
                        if (node.scaleTextWithCamera) {
                            this.updateDimensionControlPoints(node, dimensionInfo);
                        }
                        dimensionInfo.textNeedsUpdate = false;
                    }
                }
            }

            if (meshInfo.rootMesh.isEnabled() !== node.enabled) {
                meshInfo.rootMesh.setEnabled(node.enabled);
                meshInfo.line.instance?.setEnabled(node.enabled);
                meshInfo.lineCap.instance?.setEnabled(node.enabled);
            }
        }
    }

    delete3d(node: ITrackedClass<DimensionNode>, keepInStore = false) {
        const dimensionModels = this.dimensionStore.get(node._dynamicId);
        if (dimensionModels?.meshInfo) {
            this.deleteLine(dimensionModels.meshInfo);
        }
        if (!keepInStore) {
            this.dimensionStore.delete(node._dynamicId);
            this.billboards.delete(node._dynamicId);
        }
        super.delete3d(node);
    }

    dispose() {
        this.billboards.clear();
        this.dimensionStore.clear();
        super.dispose();
    }

    private deleteLine(dimensionModels: DimensionParts) {
        if (dimensionModels.line.instance) {
            dimensionModels.line.instance.dispose();
            delete dimensionModels.line.instance;
        }
        if (dimensionModels.lineCap.instance) {
            dimensionModels.lineCap.instance.dispose();
            delete dimensionModels.lineCap.instance;
        }
    }

    private updateLine(
        dimensionInfo: DimensionInfo,
        node: ITrackedClass<DimensionNode>,
        meshInfo: DimensionParts,
        scene: Scene
    ) {
        dimensionInfo.canCreate = this.updateDimensionControlPoints(node, dimensionInfo);

        // If the line is not getting rebuilt, then just update the line position
        if (dimensionInfo.canCreate && !dimensionInfo.lineNeedsUpdate) {
            meshInfo.line.instance = drawLine(node, meshInfo.line, 1, scene);
            meshInfo.lineCap.instance = MeshBuilder.CreateLineSystem(
                `${node._dynamicId}_lineCap`,
                meshInfo.lineCap,
                scene
            );
            meshInfo.lineCap.instance.isPickable = node.isPickable;
        }

        dimensionInfo.positionNeedsUpdate = false;
        if (dimensionInfo.canCreate) {
            if (dimensionInfo.lineNeedsUpdate) {
                if (meshInfo.line.instance) {
                    meshInfo.line.instance.dispose();
                    delete meshInfo.line.instance;
                }
                if (meshInfo.lineCap.instance) {
                    meshInfo.lineCap.instance.dispose();
                    delete meshInfo.lineCap.instance;
                }

                this.updateDimensionLineColor(node, meshInfo.line, meshInfo.lineCap);

                meshInfo.line.instance = drawLine(node, meshInfo.line, meshInfo.distance, scene);

                if (node.lineCapStyle !== eDimensionLineCapStyle.none) {
                    const lineCapsMesh = MeshBuilder.CreateLineSystem(
                        `${node._dynamicId}_lineCap`,
                        meshInfo.lineCap,
                        scene
                    );
                    lineCapsMesh.isPickable = node.isPickable;
                    if (!meshInfo.lineCap.instance) {
                        meshInfo.lineCap.instance = lineCapsMesh;
                    }
                }

                // Put the lines on the parent scene so they get it's visibility properties
                if (node._parentScene) {
                    const parentSceneMesh = this.viewer.getRendererNode(node._parentScene);
                    if (meshInfo.line.instance) {
                        meshInfo.line.instance.parent = parentSceneMesh;
                    }
                    if (meshInfo.lineCap.instance) {
                        meshInfo.lineCap.instance.parent = parentSceneMesh;
                    }
                }

                dimensionInfo.lineNeedsUpdate = false;
            }

            if (dimensionInfo.textNeedsUpdate) {
                this.updateDimensionText(node, meshInfo);

                dimensionInfo.textNeedsUpdate = false;
            }
        }
    }

    private updateLineFromCamera(
        node: ITrackedClass<DimensionNode>,
        meshInfo: DimensionParts,
        scene: Scene,
        options: scalingDashesOptions
    ) {
        if (meshInfo.line.instance) {
            meshInfo.line.instance.dispose();
            delete meshInfo.line.instance;
        }
        this.updateDimensionLineColor(node, meshInfo.line, meshInfo.lineCap);
        meshInfo.line.instance = drawLine(node, meshInfo.line, 1, scene, options); //CreateDashedLines('lines', meshInfo.line);
        if (node._parentScene) {
            const parentSceneMesh = this.viewer.getRendererNode(node._parentScene);
            if (meshInfo.line.instance) {
                meshInfo.line.instance.parent = parentSceneMesh;
            }
            if (meshInfo.lineCap.instance) {
                meshInfo.lineCap.instance.parent = parentSceneMesh;
            }
        }
    }

  private updateDimensionControlPoints(node: ITrackedClass<DimensionNode>, dimensionInfo: DimensionInfo) {
        if (!dimensionInfo.meshInfo) {
            return false;
        }
        const dimensionModel = dimensionInfo.meshInfo;

        const lineOptions = dimensionModel.line;
        const annotationMesh = dimensionModel.rootMesh;
        let target1: Kb3dObject | undefined;
        let target2: Kb3dObject | undefined;

        if (node.targetId && node.target2Id && node._manager) {
            const manager = node._manager;
            target1 = manager.getById(node.targetId);
            target2 = manager.getById(node.target2Id);
        }

        if (
            dimensionInfo.boundingInfo &&
            (dimensionInfo.boundingInfo.targetNode._dynamicId !== target1?._dynamicId ||
                dimensionInfo.boundingInfo.targetNode2._dynamicId !== target2?._dynamicId)
        ) {
            dimensionInfo.boundingInfo.subscription.unsubscribe();
            dimensionInfo.boundingInfo = undefined;
        }

        if (target1 && target2) {
            let position1: Vector3 | undefined;
            let position2: Vector3 | undefined;
            let direction: Vector3 | undefined;
            let angleAdjustment = 0;

            // Only supporting connectors for now, but could support direct mesh targets in the future
            if (target1 instanceof Connector && target2 instanceof Connector) {
                position1 = getPositionFromConnnector(target1, this.rendererManager, node._parentScene);
                position2 = getPositionFromConnnector(target2, this.rendererManager, node._parentScene);
                direction = target1.direction.toVec3().normalize();
                angleAdjustment = target1.angle;

                if (target1._parent && target2._parent) {
                    dimensionInfo.boundingInfo = {
                        targetNode: target1,
                        targetNode2: target2,
                        subscription: merge(
                            this.viewer.bbCache.watchForChanges(target1._parent),
                            this.viewer.bbCache.watchForChanges(target2._parent)
                        ).subscribe(() => {
                            node.setPropertyIsModified('targetId', true);
                            node.setPropertyIsModified('target2Id', true);
                        }),
                    };
                }
            }

            if (position1 && position2 && direction) {
                /** Calculate the dimension line */
                const offset = node.offset.toVec3();
                const startConnector = calculateTransform(position1, direction, angleAdjustment, offset);
                const endConnector = calculateTransform(position2, direction, angleAdjustment, offset);
                const dimensionLine = endConnector.position.subtract(startConnector.position);
                const startPostion = startConnector.position;
                // Depending on the dimension axis, just use the
                let endPosition: Vector3;
                if (node.axis === eDimensionAxis.free) {
                    endPosition = endConnector.position;
                } else {
                    if (node.axis === eDimensionAxis.x) {
                        endPosition = startPostion.add(dimensionLine.multiply(new Vector3(1, 0, 0)));
                    } else if (node.axis === eDimensionAxis.y) {
                        endPosition = startPostion.add(dimensionLine.multiply(new Vector3(0, 1, 0)));
                    } else {
                        endPosition = startPostion.add(dimensionLine.multiply(new Vector3(0, 0, 1)));
                    }
                }
                const rectifiedDimensionLine = endPosition.subtract(startPostion);
                const lineDirectionCross = Vector3.Cross(rectifiedDimensionLine, direction).normalize();

                lineOptions.points = [startPostion, endPosition];
                dimensionModel.distance = rectifiedDimensionLine.length();
                annotationMesh.position = startPostion.add(endPosition).scaleInPlace(0.5);
                annotationMesh.position.addInPlace(
                    transformVectorWithQuat(node.labelOffset.toVec3(), startConnector.rotation)
                );
                dimensionModel.textContainer.rotationQuaternion = startConnector.textRotation;

                if (!node.billboard) {
                    annotationMesh.rotationQuaternion = rotationToVector(lineDirectionCross);
                }

                // maxClampingDistance of 0 (or falsy) means no cap — scale infinitely
                const maxClamp = node.maxClampingDistance || 0;

                // Control of the sides, top and bottom margins of the text of the connectors --->
                if (node.scaleTextWithCamera) {
                    try {
                        const raw = node.labelOffset?.toVec3() ?? Vector3.Zero();

                        if (raw.length() > 1e-8) {
                            let cameraDistance = 1;
                            const camera = this.scene.activeCamera;
                            if (camera) {
                                if (camera instanceof KbArcRotateCamera && camera.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                                    cameraDistance = camera.orthoWidth;
                                } else {
                                    const cameraPosition = getCameraPositionWithOffset(camera);
                                    cameraDistance = cameraPosition.subtract(annotationMesh.position).length();
                                }
                            }

                            if (maxClamp > 0) {
                                cameraDistance = Math.min(cameraDistance, maxClamp);
                            }

                            // Use the rotated labelOffset direction so X/Z offsets and
                            // rotated annotations all push along the correct axis
                            const lineMidpoint = startPostion.add(endPosition).scale(0.5);
                            const appliedOffset = annotationMesh.position.subtract(lineMidpoint);
                            const offsetDir = appliedOffset.normalize();
                            const pushMagnitude = cameraDistance * node.fontSize / 15;

                            // For nested scenes, transform world-space push direction
                            // into the parent's local frame
                            let pushDir = offsetDir;
                            const parentNode = annotationMesh.parent;
                            if (parentNode && parentNode instanceof TransformNode) {
                                const invParentWorld = parentNode.getWorldMatrix().clone().invert();
                                const localDir = Vector3.TransformNormal(offsetDir, invParentWorld);
                                if (localDir.lengthSquared() > 1e-8) {
                                    pushDir = localDir.normalize();
                                }
                            }

                            const pushVector = pushDir.scale(pushMagnitude);
                            if (isFinite(pushVector.x) && isFinite(pushVector.y) && isFinite(pushVector.z)) {
                                annotationMesh.position.addInPlace(pushVector);
                            }
                        }
                    } catch {
                        // Skip push on failure — position is already at the correct midpoint
                    }
                }
                // <---

                /** Calculate the line caps */
                const capSize = direction.scale(node.fontSize / 3);
                if (node.scaleTextWithCamera) {
                    const camera = this.scene.activeCamera;
                    let distance = 0;
                    if (camera) {
                        let cameraPosition = new Vector3(0, 0, 0);
                        if (camera instanceof KbArcRotateCamera && camera.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                            // Calculate the real world position of the ArcRotateCamera
                            distance = camera.orthoWidth;
                        } else {
                            cameraPosition = getCameraPositionWithOffset(camera);
                            distance = cameraPosition.subtract(annotationMesh.position).length();
                        }

                        if (maxClamp > 0) {
                            distance = Math.min(distance, maxClamp);
                        }

                        capSize.scaleInPlace(distance * 0.2);
                    }
                }

                const lineToStartConnector = direction.scale(
                    Vector3.Dot(transformVectorWithQuat(offset, startConnector.rotation), direction) *
                        (1 - node.dimensionGap)
                );
                const endOffset =
                    node.axis === eDimensionAxis.free
                        ? transformVectorWithQuat(offset, startConnector.rotation)
                        : endPosition.subtract(position2);
                const lineToEndConnector = direction.scale(Vector3.Dot(endOffset, direction) * (1 - node.dimensionGap));

                dimensionModel.lineCap.lines = [];
                if (node.lineCapStyle !== eDimensionLineCapStyle.none) {
                    dimensionModel.lineCap.lines = [
                        [startPostion.subtract(lineToStartConnector), startPostion, startPostion.add(capSize)],
                        [endPosition.subtract(lineToEndConnector), endPosition, endPosition.add(capSize)],
                    ];
                }
                if (node.lineCapStyle === eDimensionLineCapStyle.arrow) {
                    const capArrowOffset = rectifiedDimensionLine.normalizeToNew().scaleInPlace(capSize.length());
                    dimensionModel.lineCap.lines.push(
                        [
                            startPostion.subtract(capSize).addInPlace(capArrowOffset),
                            startPostion,
                            startPostion.add(capSize).addInPlace(capArrowOffset),
                        ],
                        [
                            endPosition.subtract(capSize).subtractInPlace(capArrowOffset),
                            endPosition,
                            endPosition.add(capSize).subtractInPlace(capArrowOffset),
                        ]
                    );
                }

                return true;
            }
        }

        return false;
    }

    private updateDimensionLineColor(
        node: ITrackedClass<DimensionNode>,
        lineOptions: ILineMeshOptions,
        lineCapOptions: ICapMeshOptions
    ) {
        const color = node.color.toColor4();
        lineOptions.colors = [];
        for (let i = 0; i < lineOptions.points.length; i++) {
            lineOptions.colors.push(color);
        }

        lineCapOptions.colors = [];
        for (let i = 0; i < 4; i++) {
            const lineColor: Color4[] = [];
            lineCapOptions.colors.push(lineColor);
            for (let j = 0; j < 3; j++) {
                lineColor.push(color);
            }
        }
    }

    private updateLineCaps(node: ITrackedClass<DimensionNode>, meshInfo: DimensionParts, scene: Scene) {
        if (node.lineCapStyle !== eDimensionLineCapStyle.none) {
            const lineCapsMesh = MeshBuilder.CreateLineSystem(`${node._dynamicId}_lineCap`, meshInfo.lineCap, scene);
            lineCapsMesh.isPickable = node.isPickable;
            if (!meshInfo.lineCap.instance) {
                meshInfo.lineCap.instance = lineCapsMesh;
            }
        }

        // Put the lines on the parent scene so they get it's visibility properties
        if (node._parentScene) {
            const parentSceneMesh = this.viewer.getRendererNode(node._parentScene);
            if (meshInfo.line.instance) {
                meshInfo.line.instance.parent = parentSceneMesh;
            }
            if (meshInfo.lineCap.instance) {
                meshInfo.lineCap.instance.parent = parentSceneMesh;
            }
        }
    }

    private updateDimensionText(node: ITrackedClass<DimensionNode>, meshInfo: DimensionParts) {
        let distanceDefaultVal: string;
        if (node._parentScene && node._parentScene.dimensionUnitScalar && node.dimensionUnitPrecision !== undefined) {
            let distance = node._parentScene.dimensionUnitScalar * meshInfo.distance;
            const precisionMultiplier = Math.pow(10, Math.abs(node.dimensionUnitPrecision));
            distance = Math.round((distance + Number.EPSILON) * precisionMultiplier) / precisionMultiplier;
            distanceDefaultVal = distance + (node._parentScene.dimensionUnitSymbol || '');
        } else {
            let distance = meshInfo.distance;
            const precisionMultiplier = Math.pow(10, Math.abs(node.dimensionUnitPrecision));
            distance = Math.round((distance + Number.EPSILON) * precisionMultiplier) / precisionMultiplier;
            distanceDefaultVal = distance.toString(10);
        }
        updateAnnotationText(
            node,
            meshInfo.textMesh,
            meshInfo.textMaterial,
            this.scene.getEngine().webGLVersion < 2,
            distanceDefaultVal,
            { v: meshInfo.distance },
            this.viewer.scene
        );
    }
}

export function getDimensionLinePoints(node: DimensionNode, rendererManager: RendererManager) {
    if (node.targetId && node.target2Id && node._manager) {
        const manager = node._manager;
        const target1 = manager.getById(node.targetId);
        const target2 = manager.getById(node.target2Id);
        let position1: Vector3 | undefined;
        let position2: Vector3 | undefined;
        let direction: Vector3 | undefined;
        if (target1 instanceof Connector && target2 instanceof Connector) {
            position1 = getPositionFromConnnector(target1, rendererManager, node._parentScene);
            position2 = getPositionFromConnnector(target2, rendererManager, node._parentScene);
            direction = target1.direction.toVec3();
        }

        if (position1 && position2 && direction) {
            const lineStart = calculateTransform(position1, direction, 0, node.offset.toVec3());
            const lineEnd = calculateTransform(position2, direction, 0, node.offset.toVec3());
            return [lineStart.position, lineEnd.position];
        }
    }

    return undefined;
}
